// Аналитика screen — single tab shell with one `.at-tabs` bar
// (Точность 24h | сходимость). The map lives in the bottom navigation,
// not here.
//   * «Точность 24h» → Daily24hTab    (original daily-analytics view —
//                       rolled back from the Phase-2 prototype per
//                       operator feedback; no backend change)
//   * «сходимость»   → ConvergenceTab (aggregated convergence curve,
//                       keeps its separate redesign)
//
// The screen owns the filter set + tab selection (both persisted to
// localStorage so back-nav from DetailScreen restores them) and drives the
// analytics auto-refresh: the heavy sections re-fetch only when a running
// series finishes (active→terminal edge, see useAnalyticsAutoRefresh) or on the
// explicit «обновить» button — NOT on a blind 30s timer over the whole
// ['analytics'] namespace. The 5s status poll stays a separate ['status'] query.

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { AnalyticsFilters as Filters } from '../api/types';
import { Daily24hTab } from '../components/analytics/Daily24hTab';
import { ConvergenceTab } from '../components/analytics/ConvergenceTab';
import { cn } from '../lib/classnames';
import { useAnalyticsAutoRefresh } from '../hooks/useAnalyticsAutoRefresh';
import { useAvailableConfigs } from '../hooks/useAvailableConfigs';

import '../components/analytics/AnalyticsScreen.css';
import '../components/analytics/analytics-tabs.css';

type AnalyticsTab = 'daily' | 'convergence';
const TAB_STORAGE_KEY = 'gnss-qa:analytics-tab';

function loadTab(): AnalyticsTab {
  try {
    const raw = localStorage.getItem(TAB_STORAGE_KEY);
    return raw === 'convergence' ? 'convergence' : 'daily';
  } catch {
    return 'daily';
  }
}

function saveTab(t: AnalyticsTab): void {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, t);
  } catch {
    /* ignore storage failures */
  }
}

/** Phase 6.1 — localStorage key for persisting the Аналитика
 *  filter set across page reloads and back-nav from DetailScreen.
 *  ``try/catch`` everywhere around localStorage access — Telegram
 *  WebView in private mode throws on access. */
const FILTERS_STORAGE_KEY = 'gnss-qa:analytics-filters';

function loadFilters(): Filters {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return { tectonic_model: null, gnss_configs: null };
    const parsed = JSON.parse(raw) as Partial<Filters>;
    return {
      tectonic_model:
        typeof parsed.tectonic_model === 'string' ? parsed.tectonic_model : null,
      gnss_configs:
        Array.isArray(parsed.gnss_configs) &&
        parsed.gnss_configs.every((c) => typeof c === 'string')
          ? parsed.gnss_configs
          : null,
    };
  } catch {
    return { tectonic_model: null, gnss_configs: null };
  }
}

function saveFilters(filters: Filters): void {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    /* ignore storage failures */
  }
}

export function AnalyticsScreen() {
  const [filters, setFilters] = useState<Filters>(() => loadFilters());
  const [tab, setTabState] = useState<AnalyticsTab>(() => loadTab());

  const setTab = (next: AnalyticsTab) => {
    setTabState(next);
    saveTab(next);
  };

  const setAndPersistFilters = (next: Filters) => {
    setFilters(next);
    saveFilters(next);
  };

  // Phase 6.6 B — drop selected gnss_configs keys that no longer exist
  // in the backend's available list (obsolete localStorage combo).
  const availableConfigs = useAvailableConfigs();
  useEffect(() => {
    const available = availableConfigs.data?.configs;
    if (!available || filters.gnss_configs === null) return;
    const validKeys = new Set(available.map((c) => c.key));
    const pruned = filters.gnss_configs.filter((k) => validKeys.has(k));
    if (pruned.length !== filters.gnss_configs.length) {
      setAndPersistFilters({
        ...filters,
        gnss_configs: pruned.length > 0 ? pruned : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableConfigs.data]);

  const queryClient = useQueryClient();
  // C1.3 — the heavy analytics sections refresh only when a running series
  // actually finishes (active→terminal edge, inside the hook), NOT on a blind
  // 30s timer that re-fetched the whole ['analytics'] namespace on every tick.
  // The manual «обновить» button below is the explicit override.
  const { hasActiveJobs } = useAnalyticsAutoRefresh();

  return (
    <div className="analytics-screen screen">
      <div className="analytics-header">
        <h2 className="analytics-title">аналитика</h2>
        <button
          type="button"
          className="analytics-refresh-btn"
          onClick={() => void queryClient.invalidateQueries({ queryKey: ['analytics'] })}
          aria-label="Обновить аналитику"
          title="Обновить"
        >
          обновить
        </button>
      </div>

      {/* Single tab bar — Точность 24h | сходимость. */}
      <div className="at-tabs" role="tablist" data-testid="analytics-tabs">
        <button
          role="tab"
          aria-selected={tab === 'daily'}
          className={cn('at-tab', tab === 'daily' && 'active')}
          onClick={() => setTab('daily')}
          data-testid="analytics-tab-daily"
        >
          Точность 24h
        </button>
        <button
          role="tab"
          aria-selected={tab === 'convergence'}
          className={cn('at-tab', tab === 'convergence' && 'active')}
          onClick={() => setTab('convergence')}
          data-testid="analytics-tab-convergence"
        >
          сходимость
        </button>
      </div>

      {hasActiveJobs && (
        <div className="live-indicator" data-testid="live-indicator">
          серия в работе — данные обновятся по завершении
        </div>
      )}

      {tab === 'daily' && (
        <Daily24hTab filters={filters} onFilterChange={setAndPersistFilters} />
      )}
      {tab === 'convergence' && <ConvergenceTab />}
    </div>
  );
}
